import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPlus, FaUpload, FaChartLine } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const EMPTY_FORM = { title: "", description: "", type: "lesson_notes", price: "" };

const TeacherMarketplace = () => {
    const navigate = useNavigate();
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [earnings, setEarnings] = useState({});

    const fetchBundles = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/marketplace/bundles/mine`, { headers: authHeaders() });
            const data = await res.json();
            setBundles(data.data || []);
        } catch (err) {
            toast.error("Could not load your bundles");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBundles(); }, []);

    const loadEarnings = async (id) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/marketplace/bundles/${id}/earnings`, { headers: authHeaders() });
            const data = await res.json();
            setEarnings((prev) => ({ ...prev, [id]: data.data }));
        } catch (err) {
            // silent
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.price) {
            toast.error("Title and price are required");
            return;
        }
        setSaving(true);
        try {
            const body = new FormData();
            Object.entries(form).forEach(([k, v]) => body.append(k, v));
            if (file) body.append("content", file);

            const res = await fetch(`${API_BASE_URL}/api/marketplace/bundles`, {
                method: "POST",
                headers: authHeaders(),
                body,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to create bundle");
            toast.success("Bundle created as a draft");
            setForm(EMPTY_FORM);
            setFile(null);
            setShowForm(false);
            fetchBundles();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async (id) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/marketplace/bundles/${id}/publish`, {
                method: "POST",
                headers: authHeaders(),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to publish");
            toast.success("Bundle published");
            fetchBundles();
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/teacher-dashboard")} className="text-white"><FaArrowLeft size={18} /></button>
                    <h1 className="text-xl font-bold text-white">Marketplace</h1>
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="bg-white text-blue600 px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
                    <FaPlus /> New Bundle
                </button>
            </nav>

            <div className="max-w-3xl mx-auto p-6">
                {showForm && (
                    <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm p-6 space-y-4 mb-6">
                        <input required placeholder="Title" className="border rounded-lg p-2 w-full" value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        <textarea placeholder="Description" className="border rounded-lg p-2 w-full" rows={3} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        <div className="grid grid-cols-2 gap-4">
                            <select className="border rounded-lg p-2" value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}>
                                <option value="lesson_notes">Lesson Notes</option>
                                <option value="revision_guide">Revision Guide</option>
                                <option value="practice_test">Practice Test</option>
                                <option value="video">Video</option>
                                <option value="exam_prep">Exam Prep Pack</option>
                            </select>
                            <input required type="number" min={1} placeholder="Price (₦)" className="border rounded-lg p-2" value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })} />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer w-fit border rounded-lg px-3 py-2">
                            <FaUpload /> {file ? file.name : "Upload content (PDF, video, doc)"}
                            <input type="file" accept=".pdf,.mp4,.mov,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png" className="hidden"
                                onChange={(e) => setFile(e.target.files[0])} />
                        </label>
                        <button disabled={saving} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">
                            {saving ? "Saving..." : "Save Bundle"}
                        </button>
                    </form>
                )}

                {loading && <p className="text-gray-500">Loading...</p>}
                {!loading && bundles.length === 0 && <p className="text-gray-500">No bundles yet — create your first one.</p>}

                <div className="space-y-3">
                    {bundles.map((b) => (
                        <div key={b.id} className="bg-white rounded-xl shadow-sm p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-gray-800">{b.title}</h3>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                            {b.status}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500">₦{b.price.toLocaleString()}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => loadEarnings(b.id)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg" title="View earnings">
                                        <FaChartLine />
                                    </button>
                                    {b.status !== "published" && (
                                        <button onClick={() => handlePublish(b.id)} className="bg-blue600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold">
                                            Publish
                                        </button>
                                    )}
                                </div>
                            </div>
                            {earnings[b.id] && (
                                <p className="text-xs text-gray-500 mt-2 border-t pt-2">
                                    {earnings[b.id].subscribers} subscriber(s) · ₦{earnings[b.id].totalEarnings.toLocaleString()} earned
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TeacherMarketplace;
